import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { WeatherService, CurrentWeather } from "../data/weatherService";
import { getSiteByCode, SiteDetails } from "../data/sitelocations";
import { useAuth } from "../context/AuthContext";
import WeatherWidget from "./header/WeatherWidget";
import UserMenu from "./header/UserMenu";
import AppLauncher from "./AppLauncher";
import "./header/styles.css";
import backendApi from "../services/backendApi";
import { buildHomeNavigationTarget, resolveActiveSiteCode } from "../utils/navigationContext";

interface ApiSite {
  site_name?: string;
  site_code?: string;
  location?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  google_maps_url?: string | null;
  map_embed?: string | null;
}

const DEFAULT_COORDS = { lat: 27.3389, lon: 73.4347 };
const ADMIN_FIXED_SITE_INFO: SiteDetails = {
  location: "Bengaluru, Karnataka",
  coordinates: { lat: 12.9716, lon: 77.5946 },
  displayName: "Bengaluru, Karnataka - AXON",
  gmapsUrl: "https://maps.google.com/?q=12.9716,77.5946",
  mapEmbed:
    "https://maps.google.com/maps?q=12.9716,77.5946&t=k&z=16&output=embed",
};
const SITE_CACHE_PREFIX = "site-meta:";
const SITE_CACHE_TTL_MS = 1000 * 60 * 60 * 12; // 12 hours

const parseCoordinate = (value?: number | string | null): number | null => {
  if (typeof value === "number") {
    return isNaN(value) ? null : value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

// Reverse geocode coordinates to get location name
const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
  try {
    // Use OpenStreetMap Nominatim API (free, no API key required)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'AxonMetric-WebApp/1.0' // Required by Nominatim
        }
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    if (data && data.address) {
      const addr = data.address;
      // Build location string: city, state, country
      const parts: string[] = [];
      if (addr.city) parts.push(addr.city);
      else if (addr.town) parts.push(addr.town);
      else if (addr.village) parts.push(addr.village);

      if (addr.state) parts.push(addr.state);
      else if (addr.region) parts.push(addr.region);

      if (addr.country) parts.push(addr.country);

      return parts.length > 0 ? parts.join(", ") : data.display_name || null;
    }
    return data.display_name || null;
  } catch (error) {
return null;
  }
};

const buildSiteDetailsFromApi = async (
  site: ApiSite,
  fallback: SiteDetails | null,
  cachedLocationName?: string
): Promise<SiteDetails> => {
  const lat = parseCoordinate(site.latitude);
  const lon = parseCoordinate(site.longitude);
  const fallbackCoords = fallback?.coordinates || DEFAULT_COORDS;
  const coords =
    lat !== null && lon !== null ? { lat, lon } : fallbackCoords;

  // If we have valid coordinates from database, use reverse geocoding to get actual location name
  // This ensures we show the correct location based on coordinates, not hardcoded database values
  let location: string;
  if (lat !== null && lon !== null) {
    // Use cached location name if available, otherwise reverse geocode
    if (cachedLocationName) {
      location = cachedLocationName;
    } else {
      // Try reverse geocoding
      const geocodedLocation = await reverseGeocode(coords.lat, coords.lon);
      if (geocodedLocation) {
        location = geocodedLocation;
      } else {
        // Fallback to coordinates if reverse geocoding fails
        location = `${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)}`;
      }
    }
  } else {
    // No coordinates available, use database location or fallback
    location =
      site.location ||
      fallback?.location ||
      `${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)}`;
  }

  const displayName = site.site_name
    ? site.site_name
    : fallback?.displayName || site.site_code || "Site";

  const gmapsUrl =
    site.google_maps_url ||
    `https://maps.google.com/?q=${coords.lat},${coords.lon}`;

  // If we have valid coordinates from database, generate map embed from coordinates
  // This ensures the map shows the location entered by admin, not old hardcoded embeds
  // Only use stored map_embed if coordinates are not available (fallback case)
  const mapEmbed =
    (lat !== null && lon !== null)
      ? `https://maps.google.com/maps?q=${coords.lat},${coords.lon}&t=k&z=16&output=embed`
      : (site.map_embed || fallback?.mapEmbed || "");

  return {
    location,
    coordinates: coords,
    displayName,
    gmapsUrl,
    mapEmbed
  };
};

const getCachedSiteMeta = (siteCode: string): { site: ApiSite; locationName?: string } | null => {
  if (typeof window === "undefined") return null;
  try {
    const cachedRaw = localStorage.getItem(`${SITE_CACHE_PREFIX}${siteCode}`);
    if (!cachedRaw) return null;
    const parsed = JSON.parse(cachedRaw);
    if (
      !parsed?.cachedAt ||
      Date.now() - parsed.cachedAt > SITE_CACHE_TTL_MS ||
      !parsed.site
    ) {
      localStorage.removeItem(`${SITE_CACHE_PREFIX}${siteCode}`);
      return null;
    }
    return {
      site: parsed.site as ApiSite,
      locationName: parsed.locationName
    };
  } catch {
    localStorage.removeItem(`${SITE_CACHE_PREFIX}${siteCode}`);
    return null;
  }
};

const cacheSiteMeta = (siteCode: string, site: ApiSite, locationName?: string) => {
  if (typeof window === "undefined") return;
  try {
    const cacheData: any = { cachedAt: Date.now(), site };
    if (locationName) {
      cacheData.locationName = locationName;
    }
    localStorage.setItem(
      `${SITE_CACHE_PREFIX}${siteCode}`,
      JSON.stringify(cacheData)
    );
  } catch (error) {
}
};

// Export function to clear site cache (for use after site updates)
export const clearSiteCache = (siteCode?: string) => {
  if (typeof window === "undefined") return;
  try {
    if (siteCode) {
      // Clear specific site cache
      const cacheKey = `${SITE_CACHE_PREFIX}${siteCode.toUpperCase()}`;
      localStorage.removeItem(cacheKey);
      // Trigger custom event for same-tab refresh
      window.dispatchEvent(new CustomEvent('siteCacheCleared', { detail: siteCode.toUpperCase() }));
    } else {
      // Clear all site caches
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(SITE_CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      // Trigger custom event for same-tab refresh
      window.dispatchEvent(new CustomEvent('siteCacheCleared', { detail: null }));
    }
  } catch (error) {
}
};

const PrivateHeader: React.FC = () => {
  const { site } = useParams<{ site?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Respect explicit URL context first, then selected tenant context from storage, then user default site.
  const siteCode = resolveActiveSiteCode({
    routeSite: site,
    userSiteCode: user?.siteCode,
    userTenant: user?.tenant,
    preferStoredContext: true
  });

  useEffect(() => {
    if (siteCode) {
      localStorage.setItem("currentSite", siteCode);
    }
  }, [siteCode]);
  const fallbackSiteInfo = useMemo(() => getSiteByCode(siteCode), [siteCode]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [weatherData, setWeatherData] = useState<CurrentWeather | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [resolvedSiteInfo, setResolvedSiteInfo] = useState<SiteDetails | null>(
    fallbackSiteInfo
  );
  const weatherLastFetchRef = useRef<number>(0);

  const menuRef = useRef<HTMLDivElement>(null);
  const isOnNavigationPage = !!siteCode && location.pathname === `/navigate/${siteCode}`;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const headerSiteInfo = isAdminRoute ? ADMIN_FIXED_SITE_INFO : resolvedSiteInfo;

  useEffect(() => {
    setResolvedSiteInfo(fallbackSiteInfo);
  }, [fallbackSiteInfo]);

  useEffect(() => {
    let isMounted = true;

    const lookupSiteCoordinates = async () => {
      if (!siteCode) return;
      const normalizedCode = siteCode.toUpperCase();

      const cached = getCachedSiteMeta(normalizedCode);
      if (cached) {
        const details = await buildSiteDetailsFromApi(cached.site, fallbackSiteInfo, cached.locationName);
        if (isMounted) {
          setResolvedSiteInfo(details);
        }
        return;
      }

      try {
        const response = await backendApi.getSites(false, normalizedCode);
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite =
            response.sites.find(
              (item: ApiSite) =>
                item.site_code &&
                item.site_code.toUpperCase() === normalizedCode
            ) || response.sites[0];

          if (matchedSite) {
            // Build site details and cache the location name
            const details = await buildSiteDetailsFromApi(
              matchedSite,
              fallbackSiteInfo
            );
            // Cache site metadata with the resolved location name
            cacheSiteMeta(normalizedCode, matchedSite, details.location);
            if (isMounted) {
              setResolvedSiteInfo(details);
            }
          }
        }
      } catch (error) {
}
    };

    lookupSiteCoordinates();

    // Listen for storage events to refresh when cache is cleared from another tab/component
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith(SITE_CACHE_PREFIX) && e.key.includes(siteCode.toUpperCase())) {
        // Cache was cleared, refresh site data
        lookupSiteCoordinates();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event for same-tab cache clearing
    const handleCacheCleared = (e: CustomEvent) => {
      if (!e.detail || e.detail === siteCode.toUpperCase()) {
        // Cache was cleared for this site or all sites, refresh
        lookupSiteCoordinates();
      }
    };

    window.addEventListener('siteCacheCleared' as any, handleCacheCleared);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('siteCacheCleared' as any, handleCacheCleared);
    };
  }, [siteCode, fallbackSiteInfo]);

  // Close menu when clicking outside (but not on hover leave - that's handled by UserMenu)
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        // Only close on click, not on hover
        setMenuOpen(false);
      }
    };

    // Use click event instead of mousedown to be less aggressive
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpen]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data - Use default coordinates if site info not found.
  useEffect(() => {
    const fetchWeather = async () => {
      // Avoid duplicate back-to-back calls caused by React StrictMode in development.
      const now = Date.now();
      if (now - weatherLastFetchRef.current < 2500) {
        return;
      }
      weatherLastFetchRef.current = now;

      const coords = headerSiteInfo?.coordinates || DEFAULT_COORDS;

      try {
        setWeatherLoading(true);
        const data = await WeatherService.getCurrentWeather(
          coords.lat,
          coords.lon
        );
        setWeatherData(data || null);
      } catch (error) {
setWeatherData(null);
      } finally {
        setWeatherLoading(false);
      }
    };

    // Always fetch weather (with default coordinates if site info unavailable)
    fetchWeather();
    const intervalId = setInterval(fetchWeather, 10 * 60 * 1000); // Refresh every 10 min
    return () => clearInterval(intervalId);
  }, [headerSiteInfo?.coordinates?.lat, headerSiteInfo?.coordinates?.lon]);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate("/");
  };

  const handleSiteInfo = () => {
    setMenuOpen(false);
    if (!siteCode) {
      navigate(
        buildHomeNavigationTarget({
          user,
          isGlobalAdmin: !!user?.isGlobalAdmin,
          siteCode
        })
      );
      return;
    }
    navigate(`/site-info/${siteCode}`);
  };

  const handleBackToNavigation = () => {
    setMenuOpen(false);
    navigate(
      buildHomeNavigationTarget({
        user,
        isGlobalAdmin: !!user?.isGlobalAdmin,
        siteCode
      })
    );
  };

  const displayName = user?.fullName || user?.username || 'User';
  const displayRole = user?.role || 'User';

  // Inject CSS directly into document head to override any conflicting styles
  useEffect(() => {
    const styleId = 'header-light-text-styles';

    // Remove existing style if present
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject new style
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      html body header span,
      html body .header-glass span,
      .header-light-text {
        font-weight: 400;
        font-family: 'IBM Plex Sans', -apple-system, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup on unmount
    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Baseline header text styling */
          html body header span,
          html body .header-glass span,
          .header-light-text {
            font-weight: 400;
            font-family: 'IBM Plex Sans', -apple-system, sans-serif !important;
          }
        `
      }} />
      <header className="fixed top-0 left-0 right-0 header-glass" style={{ zIndex: 9999 }}>
        <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 h-16">
          {/* Left Section: Ellipsis Menu + Logo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Ellipsis / App Launcher Menu */}
            <AppLauncher site={siteCode} />

            <img
              src="/Axon-logo.png"
              alt="AxonMetric Logo"
              className="h-8 sm:h-10 hover-lift cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">

            {/* Weather Widget - Always visible for all users */}
            <div className="hidden md:block min-w-0">
              <WeatherWidget
                weatherData={weatherData}
                weatherLoading={weatherLoading}
                currentTime={currentTime}
                siteInfo={headerSiteInfo}
                onHomeClick={handleBackToNavigation}
                showHomeButton={false}
              />
            </div>

            {/* User Menu - Pass resolved siteCode instead of URL param */}
            <div ref={menuRef} style={{ position: 'relative', zIndex: 10001 }}>
              <UserMenu
                displayName={displayName}
                displayRole={displayRole}
                site={siteCode}
                menuOpen={menuOpen}
                onToggleMenu={() => {
                  setMenuOpen(v => !v);
                }}
                onSiteInfo={handleSiteInfo}
                onLogout={handleLogout}
              />
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default PrivateHeader;

